import * as React from "react";
import { PolarAngleAxis, RadialBar, RadialBarChart } from "recharts";

import { cn } from "../../utils/utils";
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "../ui/chart";

export interface RadialScoreDatum {
    /** Chave da métrica (deve existir no `config`). */
    key: string;
    /** Valor já normalizado em 0–100 (percentual). */
    value: number;
}

interface RadialScoreChartProps {
    data: RadialScoreDatum[];
    /** Rótulos + cores das métricas. A cor vira `--color-<key>`. */
    config: ChartConfig;
    /** Conteúdo central opcional (ex.: total/headline). */
    centerLabel?: React.ReactNode;
    centerSub?: React.ReactNode;
    className?: string;
}

/**
 * Anéis radiais concêntricos (estilo "scores"): um anel por métrica, com trilho de fundo,
 * cantos arredondados e rótulos embaixo. Cores temáveis via `config`.
 */
export function RadialScoreChart({
    data,
    config,
    centerLabel,
    centerSub,
    className,
}: RadialScoreChartProps) {
    // Anel externo = primeira métrica. Cada linha vira um anel concêntrico.
    // Sem dados (tudo 0): barras transparentes (só o track aparece) — sem fiapos coloridos.
    const allZero = data.every((d) => (d.value ?? 0) <= 0);

    const chartData = data.map((d) => ({
        key: d.key,
        value: Math.max(0, Math.min(100, d.value)),
        fill: allZero ? "transparent" : `var(--color-${d.key})`,
    }));

    return (
        <div className={cn("flex h-full w-full flex-col", className)}>
            <div className="relative flex-1">
                <ChartContainer
                    config={config}
                    className="aspect-auto h-full w-full [&_.recharts-radial-bar-sectors_path]:[filter:drop-shadow(0_1px_4px_hsl(var(--foreground)/0.18))]"
                >
                    <RadialBarChart
                        data={chartData}
                        startAngle={90}
                        endAngle={-270}
                        innerRadius="40%"
                        outerRadius="100%"
                        barSize={14}
                    >
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />
                        <ChartTooltip
                            cursor={false}
                            content={
                                <ChartTooltipContent
                                    nameKey="key"
                                    formatter={(value, name) => (
                                        <div className="flex w-full items-center justify-between gap-3">
                                            <span className="text-muted-foreground">
                                                {config[name as keyof typeof config]?.label ?? name}
                                            </span>
                                            <span className="font-mono font-medium tabular-nums text-foreground">
                                                {Number(value).toFixed(0)}%
                                            </span>
                                        </div>
                                    )}
                                />
                            }
                        />
                        <RadialBar
                            dataKey="value"
                            background={{ fill: "hsl(var(--muted-foreground) / 0.2)" }}
                            cornerRadius={allZero ? 0 : 8}
                            label={{
                                position: "insideStart",
                                fill: "#fff",
                                fontSize: 11,
                                fontWeight: 700,
                                formatter: (v: number) => (v >= 6 ? `${Math.round(v)}%` : ""),
                            }}
                        />
                    </RadialBarChart>
                </ChartContainer>

                {(centerLabel || centerSub) && (
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
                        {centerLabel && (
                            <span className="text-2xl font-bold leading-none text-foreground">{centerLabel}</span>
                        )}
                        {centerSub && (
                            <span className="mt-1 text-xs text-muted-foreground">{centerSub}</span>
                        )}
                    </div>
                )}
            </div>

            <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
                {data.map((d) => (
                    <li key={d.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span
                            className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                            style={{ backgroundColor: `var(--color-${d.key})` }}
                        />
                        <span>{config[d.key]?.label ?? d.key}</span>
                        <span className="font-semibold text-foreground tabular-nums">
                            {Math.round(d.value)}%
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
